/**
 * Phase 3 — OptimisationStudyDetailPage
 *
 * Live progress while running, hero card + tornado + candidate table
 * on terminal. Polls every 3s while non-terminal.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Trash2, FileText, Crosshair } from "lucide-react";
import { Button } from "@/components/ui/button";
import PageHeader from "@/components/PageHeader";
import StatusPill from "@/components/StatusPill";
import optimisation from "@/api/optimisation";
import OptimisationProgress from "@/components/optimisation/OptimisationProgress";
import BaselineVsBestCard from "@/components/optimisation/BaselineVsBestCard";
import CandidateTable from "@/components/optimisation/CandidateTable";
import SensitivityTornadoChart from "@/components/optimisation/SensitivityTornadoChart";
import PromoteConfigurationModal from "@/components/optimisation/PromoteConfigurationModal";
import RobustnessCard from "@/components/optimisation/RobustnessCard";
import LimitingVariablesPanel from "@/components/optimisation/LimitingVariablesPanel";
import RecommendationScorePanel from "@/components/optimisation/RecommendationScorePanel";
import FailureModeBreakdownPanel from "@/components/optimisation/FailureModeBreakdownPanel";
import ParetoFrontierChart from "@/components/optimisation/ParetoFrontierChart";
import ReportPanel from "@/components/optimisation/ReportPanel";
import { formatApiError } from "@/lib/formatApiError";

const TERMINAL = ["succeeded", "failed", "cancelled", "partial"];
const PAGE_SIZE = 25;

export default function OptimisationStudyDetailPage() {
    const { studyId } = useParams();
    const navigate = useNavigate();

    const [study, setStudy] = useState(null);
    const [loadErr, setLoadErr] = useState(null);
    const [diff, setDiff] = useState(null);
    const [diffErr, setDiffErr] = useState(null);
    const [candidates, setCandidates] = useState([]);
    const [page, setPage] = useState(0);
    // Phase 3.6 — default sort flips to recommendation_score DESC.
    const [sort, setSort] = useState({ field: "recommendation_score", dir: "desc" });
    const [events, setEvents] = useState([]);
    const [artifacts, setArtifacts] = useState([]);
    const [cancelling, setCancelling] = useState(false);
    const [promoteOpen, setPromoteOpen] = useState(false);
    const [promoteCandId, setPromoteCandId] = useState(null);
    const pollRef = useRef(null);

    const isTerminal = study && TERMINAL.includes(study.status);

    const fetchStudy = useCallback(async () => {
        try {
            const s = await optimisation.getStudy(studyId);
            setStudy(s);
            return s;
        } catch (e) {
            setLoadErr(
                e?.response?.status === 404
                    ? "Study not found in this tenant."
                    : formatApiError(e, "Failed to load study"),
            );
            return null;
        }
    }, [studyId]);

    const fetchSecondary = useCallback(async (s) => {
        if (!s) return;
        try {
            const cands = await optimisation.listCandidates(s.id, {
                limit: PAGE_SIZE, skip: page * PAGE_SIZE,
            });
            setCandidates(cands);
        } catch (_) {
            // candidates endpoint may briefly 404 immediately after creation
        }
        try {
            const ev = await optimisation.listEvents(s.id, { limit: 50 });
            setEvents(ev);
        } catch (_) { /* ignore */ }
        try {
            const arts = await optimisation.listArtifacts(s.id);
            setArtifacts(arts);
        } catch (_) { /* ignore */ }
        if (TERMINAL.includes(s.status)) {
            try {
                const d = await optimisation.getDiff(s.id);
                setDiff(d);
                setDiffErr(null);
            } catch (e) {
                if (e?.response?.status === 409) {
                    setDiffErr(e.response.data?.detail?.reason || "No recommendation yet.");
                } else {
                    setDiffErr(e?.message || "Failed to load diff");
                }
                setDiff(null);
            }
        }
    }, [page]);

    // Phase 4c — caller-triggered refresh for the artefact list (used by
    // the ReportPanel after a successful generate/regenerate).
    const refreshArtifacts = useCallback(async () => {
        try {
            const arts = await optimisation.listArtifacts(studyId);
            setArtifacts(arts);
        } catch (_) { /* ignore */ }
    }, [studyId]);

    // Initial + polling fetch
    useEffect(() => {
        let cancelled = false;
        const loop = async () => {
            const s = await fetchStudy();
            if (cancelled) return;
            await fetchSecondary(s);
            if (!cancelled && s && !TERMINAL.includes(s.status)) {
                pollRef.current = setTimeout(loop, 3000);
            }
        };
        loop();
        return () => {
            cancelled = true;
            if (pollRef.current) clearTimeout(pollRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [studyId, page]);

    const handleCancel = async () => {
        setCancelling(true);
        try {
            await optimisation.cancelStudy(studyId);
            toast.info("Cancellation requested.");
            await fetchStudy();
        } catch (e) {
            toast.error(formatApiError(e, "Cancel failed"));
        } finally {
            setCancelling(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm("Delete this study and its candidates?")) return;
        try {
            await optimisation.deleteStudy(studyId);
            toast.success("Study deleted.");
            navigate("/run/optimisation");
        } catch (e) {
            toast.error(formatApiError(e, "Delete failed"));
        }
    };

    const openPromote = (candId) => {
        setPromoteCandId(candId);
        setPromoteOpen(true);
    };

    const objectiveLabel = useMemo(() => {
        const t = study?.objective?.type || "maximise_pk";
        const target = study?.objective?.target_pk;
        const map = {
            maximise_pk: "Maximise probability of kill",
            reach_target_pk: target
                ? `Reach Pk ≥ ${target}`
                : "Reach a target Pk",
            minimise_cost_for_target_pk: target
                ? `Minimise cost for Pk ≥ ${target}`
                : "Minimise cost for a target Pk",
            minimise_miss_distance: "Minimise mean miss distance",
            maximise_efficiency: "Maximise design efficiency",
            maximise_robustness: "Maximise robustness",
        };
        return map[t] || t;
    }, [study]);

    if (loadErr) {
        return (
            <div className="p-6" data-testid="optimisation-detail-error">
                <div className="rounded-md border border-ux-danger/40 bg-ux-danger-soft/30 p-4 text-[13px] text-ux-danger">
                    <p className="font-medium">Unable to open study.</p>
                    <p className="text-[12px] mt-1">{loadErr}</p>
                    <Button
                        variant="outline"
                        size="sm"
                        className="mt-3"
                        onClick={() => navigate("/run/optimisation")}
                    >
                        Back to studies
                    </Button>
                </div>
            </div>
        );
    }

    if (!study) {
        return (
            <div className="p-12 text-center text-ux-text-3 text-[12px]">
                Loading study…
            </div>
        );
    }

    return (
        <div className="space-y-4 pb-12" data-testid="optimisation-detail-page">
            <PageHeader
                icon={Crosshair}
                title={study.name}
                description={
                    <span className="flex items-baseline gap-3 flex-wrap">
                        <code className="text-[10px] font-mono text-ux-text-3">
                            {study.id}
                        </code>
                        <span className="text-[11px] text-ux-text-3">
                            owner: {study.owner_username || "—"}
                        </span>
                        <span className="text-[11px] text-ux-text-3">
                            {study.created_at?.slice(0, 16) || "—"}
                        </span>
                        <span className="text-[11px] text-ux-text-2">
                            {objectiveLabel}
                        </span>
                    </span>
                }
                actions={
                    <div className="flex items-center gap-2 flex-wrap">
                        <StatusPill status={study.status} testId="study-status-pill" />
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate("/run/optimisation")}
                        >
                            <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Back
                        </Button>
                        {isTerminal && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleDelete}
                                data-testid="optimisation-detail-delete"
                            >
                                <Trash2 className="w-3 h-3 mr-1" /> Delete
                            </Button>
                        )}
                    </div>
                }
            />

            {!isTerminal && (
                <OptimisationProgress
                    study={study}
                    onCancel={handleCancel}
                    cancelling={cancelling}
                />
            )}

            {/* Phase 3.6 — partial-study limiting-variables banner */}
            {isTerminal && study.status === "partial" && (
                <LimitingVariablesPanel
                    studyStatus={study.status}
                    limitingVariables={
                        diff?.limiting_variables || study?.limiting_variables || []
                    }
                />
            )}

            {isTerminal && diff && (
                <BaselineVsBestCard
                    diff={diff}
                    onPromote={openPromote}
                />
            )}

            {/* Phase 3.6 — composite recommendation score panel */}
            {isTerminal && diff?.recommended?.recommendation_score !== undefined && (
                <RecommendationScorePanel diff={diff} />
            )}

            {/* Phase 4c — Pareto frontier scatter (always render after a
                terminal study so the empty-state copy is visible too). */}
            {isTerminal && (
                <ParetoFrontierChart
                    candidates={candidates}
                    kneeCandidateId={diff?.pareto?.knee_candidate_id || null}
                    baselinePk={diff?.baseline?.pk ?? study?.baseline_metrics?.pk ?? null}
                    bestCandidateId={study?.best_candidate_id || null}
                />
            )}

            {isTerminal && !diff && diffErr && (
                <div
                    className="rounded-md border border-ux-warning/40 bg-ux-warning-soft/30 p-4 text-[12px] text-ux-warning"
                    data-testid="optimisation-detail-no-diff"
                >
                    {diffErr}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 space-y-4">
                    {isTerminal && (
                        <SensitivityTornadoChart studyId={study.id} />
                    )}
                    <CandidateTable
                        candidates={candidates}
                        canPromote={isTerminal && study.status === "succeeded"}
                        onPromote={openPromote}
                        bestCandidateId={study.best_candidate_id}
                        page={page}
                        pageSize={PAGE_SIZE}
                        onPageChange={setPage}
                        sort={sort}
                        onSortChange={setSort}
                    />
                    {/* Phase 3.6 — robustness profile (only on robustness studies) */}
                    {isTerminal && diff?.recommended?.robustness_components && (
                        <RobustnessCard diff={diff} />
                    )}
                    {/* Phase 3.6 — failure-mode breakdown */}
                    {isTerminal && diff?.recommended?.failure_mode_distribution && (
                        <FailureModeBreakdownPanel diff={diff} />
                    )}
                </div>
                <aside className="space-y-3">
                    <ReportPanel
                        studyId={study.id}
                        studyStatus={study.status}
                        artifacts={artifacts}
                        onGenerated={refreshArtifacts}
                    />
                    <ArtifactsList artifacts={artifacts} studyId={study.id} />
                    <EventsList events={events} />
                </aside>
            </div>

            <PromoteConfigurationModal
                open={promoteOpen}
                onOpenChange={setPromoteOpen}
                studyId={study.id}
                studyName={study.name}
                candidateId={promoteCandId}
            />
        </div>
    );
}

function ArtifactsList({ artifacts, studyId }) {
    return (
        <div
            className="rounded-md border border-ux-border-1 bg-ux-bg-1 p-3 space-y-2"
            data-testid="optimisation-detail-artifacts"
        >
            <h3 className="text-[11px] uppercase tracking-wider text-ux-text-3">
                Artefacts
            </h3>
            {artifacts.length === 0 ? (
                <p className="text-[11px] text-ux-text-3 italic">None yet.</p>
            ) : (
                <ul className="space-y-1">
                    {artifacts.map((a) => (
                        <li
                            key={a.name}
                            className="text-[11px] text-ux-text-2 flex items-center gap-1"
                        >
                            <FileText className="w-3 h-3 text-ux-text-3 flex-shrink-0" />
                            <a
                                href={optimisation.artifactDownloadUrl(studyId, a.name)}
                                target="_blank"
                                rel="noreferrer"
                                className="hover:underline truncate flex-1 font-mono"
                            >
                                {a.name}
                            </a>
                            <span className="text-ux-text-3 tabular-nums">
                                {a.size_bytes ? `${Math.round(a.size_bytes / 1024)} kB` : ""}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function EventsList({ events }) {
    return (
        <div
            className="rounded-md border border-ux-border-1 bg-ux-bg-1 p-3 space-y-2 max-h-[400px] overflow-y-auto"
            data-testid="optimisation-detail-events"
        >
            <h3 className="text-[11px] uppercase tracking-wider text-ux-text-3 sticky top-0 bg-ux-bg-1">
                Events
            </h3>
            {events.length === 0 ? (
                <p className="text-[11px] text-ux-text-3 italic">No events yet.</p>
            ) : (
                <ul className="space-y-1">
                    {events.slice().reverse().map((e) => (
                        <li
                            key={e.id}
                            className="text-[11px] text-ux-text-2 flex items-start gap-2"
                        >
                            <span
                                className={[
                                    "w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0",
                                    e.level === "error"
                                        ? "bg-ux-danger"
                                        : e.level === "warning"
                                          ? "bg-ux-warning"
                                          : "bg-ux-info",
                                ].join(" ")}
                            />
                            <div className="min-w-0">
                                <p className="truncate">
                                    {e.stage && (
                                        <code className="text-[10px] font-mono text-ux-text-3 mr-1">
                                            {e.stage}
                                        </code>
                                    )}
                                    {e.message}
                                </p>
                                {e.created_at && (
                                    <span className="text-[10px] font-mono text-ux-text-3">
                                        {e.created_at.slice(11, 19)}
                                    </span>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
